import type { ReactNode } from 'react'

import { type useLoadMore } from '@/lib/use-load-more'
import { LoadMoreSentinel } from '@/ui/load-more-sentinel'

import type { QueryCell, ReportRunResult } from '../lib/query-result'
import { REPORT_RUN_STATE_TITLES, reportErrorMessage, reportRunState } from '../lib/report-errors'
import { QueryResultsGrid, type GridSort } from './query-results-grid'

export type ReportResultsProps = {
  result: ReportRunResult | undefined
  error: unknown
  isPending: boolean
  hasRun: boolean
  labels?: Record<string, string>
  pager?: ReturnType<typeof useLoadMore<QueryCell[]>>
  formatCell?: (column: string, value: QueryCell) => string | undefined
  sort?: GridSort
  truncatedMessage?: string
  emptyMessage?: ReactNode
  status?: ReactNode
}

export function ReportResults({ result, error, isPending, hasRun, labels, pager, formatCell, sort, truncatedMessage, emptyMessage, status }: ReportResultsProps) {
  const metrics = result && !error
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {metrics || status ? (
        <div className="border-border flex items-center gap-3 border-b px-4 py-2 text-xs">
          <div className="min-w-0 flex-1">{status}</div>
          {metrics ? (
            <p className="text-muted-foreground shrink-0 tabular-nums">
              {`${result.rowCount.toLocaleString()} ${result.rowCount === 1 ? 'row' : 'rows'} in ${result.durationMs.toLocaleString()} ms`}
            </p>
          ) : null}
        </div>
      ) : null}

      {result?.truncated ? (
        <p role="status" className="border-border bg-muted/40 text-foreground border-b px-4 py-2 text-xs">
          {result.pageInfo
            ? 'This page contains part of the report. Scroll to load more rows.'
            : `Showing the first ${result.rowCount.toLocaleString()} rows. ${truncatedMessage ?? 'This report matched more than that; add a filter or summary to narrow it.'}`}
        </p>
      ) : null}

      <ResultBody result={result} error={error} isPending={isPending} hasRun={hasRun} labels={labels} pager={pager} formatCell={formatCell} sort={sort} emptyMessage={emptyMessage} />
    </div>
  )
}

function ResultBody({ result, error, isPending, hasRun, labels, pager, formatCell, sort, emptyMessage }: ReportResultsProps) {
  if (isPending) return <p role="status" className="text-muted-foreground p-5 text-xs">Running the report…</p>

  if (error) {
    const state = reportRunState(error)
    return (
      <div role="alert" data-run-state={state} className="p-5">
        <p className="text-destructive text-sm font-medium">{REPORT_RUN_STATE_TITLES[state]}</p>
        <p className="text-muted-foreground mt-1 text-xs">{reportErrorMessage(error)}</p>
      </div>
    )
  }

  if (!hasRun || !result)
    return <p role="status" className="text-muted-foreground p-5 text-xs">Choose fields, then run the report to see rows.</p>

  if (result.rows.length === 0)
    return <div role="status" data-run-state="empty" className="text-muted-foreground p-5 text-xs">{emptyMessage ?? 'No rows match this report.'}</div>

  return (
    <QueryResultsGrid
      result={result}
      label="Report results"
      labels={labels}
      hasMore={pager?.hasNextPage ?? false}
      footer={pager ? (scrollElement) => <LoadMoreSentinel pager={pager} root={scrollElement} className="m-3" /> : undefined}
      formatCell={formatCell}
      sort={sort}
    />
  )
}
